<script setup lang="ts">
import {
  BadgeCheck,
  FileCheck2,
  HandCoins,
  MessageSquareLock,
  ReceiptText,
  ShieldCheck,
  WalletCards,
} from "lucide-vue-next";

const items = [
  {
    icon: FileCheck2,
    title: "worker_profile.safety_scope_title",
    text: "worker_profile.safety_scope_text",
  },
  {
    icon: HandCoins,
    title: "worker_profile.safety_payment_title",
    text: "worker_profile.safety_payment_text",
  },
  {
    icon: MessageSquareLock,
    title: "worker_profile.safety_records_title",
    text: "worker_profile.safety_records_text",
  },
];
</script>

<template>
  <section
    class="lg:mt-14 overflow-hidden rounded-2xl border border-[#DCE5E1] bg-[#F2F6F4] px-5 py-7 sm:px-8 sm:py-9 lg:px-10 lg:py-10"
    role="note"
  >
    <!-- Main information -->
    <div
      class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center lg:gap-12"
    >
      <div class="flex min-w-0 items-start gap-4 sm:gap-5">
        <span
          class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-sm"
        >
          <ShieldCheck class="size-5" />
        </span>

        <div class="min-w-0 max-w-3xl">
          <h2
            class="text-2xl font-bold leading-tight text-app-text sm:text-3xl"
          >
            {{ $t("worker_profile.safety_title") }}
          </h2>

          <p
            class="mt-3 text-sm leading-6 text-app-muted sm:text-base sm:leading-7"
          >
            {{ $t("worker_profile.safety_description") }}
          </p>
        </div>
      </div>

      <!-- Decorative safety illustration -->
      <div
        aria-hidden="true"
        class="relative hidden h-[210px] overflow-hidden rounded-2xl border border-app-border bg-white lg:block"
      >
        <!-- Background shapes -->
        <div
          class="absolute -right-12 -top-16 size-48 rounded-full bg-primary-100/80"
        />
        <div
          class="absolute -bottom-20 -left-12 size-52 rounded-full bg-primary-50"
        />

        <!-- Central shield -->
        <div
          class="absolute left-1/2 top-1/2 flex size-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[24px] bg-primary text-white shadow-lg"
        >
          <ShieldCheck class="size-9" />
        </div>

        <!-- Agreement card -->
        <div
          class="absolute left-6 top-6 flex items-center gap-3 rounded-xl border border-app-border bg-white px-3.5 py-3 shadow-sm"
        >
          <span
            class="flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary"
          >
            <ReceiptText class="size-4" />
          </span>

          <div class="space-y-1.5">
            <div class="h-2 w-20 rounded-full bg-app-border" />
            <div class="h-2 w-12 rounded-full bg-primary-100" />
          </div>
        </div>

        <!-- Payment card -->
        <div
          class="absolute bottom-6 left-9 flex items-center gap-3 rounded-xl border border-app-border bg-white px-3.5 py-3 shadow-sm"
        >
          <span
            class="flex size-9 items-center justify-center rounded-lg bg-primary-50 text-primary"
          >
            <WalletCards class="size-4" />
          </span>

          <div class="space-y-1.5">
            <div class="h-2 w-16 rounded-full bg-app-border" />
            <div class="h-2 w-10 rounded-full bg-primary-100" />
          </div>
        </div>

        <!-- Verified card -->
        <div
          class="absolute right-6 top-8 flex size-12 items-center justify-center rounded-xl border border-app-border bg-white text-primary shadow-sm"
        >
          <BadgeCheck class="size-6" />
        </div>

        <!-- Messages card -->
        <div
          class="absolute bottom-7 right-7 flex items-center gap-2.5 rounded-xl border border-app-border bg-white px-3 py-2.5 shadow-sm"
        >
          <MessageSquareLock class="size-4 text-primary" />

          <div class="space-y-1.5">
            <div class="h-2 w-14 rounded-full bg-app-border" />
            <div class="h-2 w-9 rounded-full bg-primary-100" />
          </div>
        </div>

        <!-- Decorative connection lines -->
        <svg
          class="pointer-events-none absolute inset-0 size-full"
          viewBox="0 0 360 210"
          fill="none"
        >
          <path
            d="M125 65C145 72 154 83 164 96"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="4 5"
            class="text-primary-200"
          />

          <path
            d="M236 65C218 73 207 83 197 96"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="4 5"
            class="text-primary-200"
          />

          <path
            d="M127 158C147 151 157 140 166 126"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="4 5"
            class="text-primary-200"
          />

          <path
            d="M238 158C219 150 208 140 197 126"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="4 5"
            class="text-primary-200"
          />
        </svg>
      </div>
    </div>

    <!-- Safety recommendations -->
    <div class="mt-8 border-t border-app-border pt-7 sm:mt-9 sm:pt-8">
      <div class="grid lg:grid-cols-3 lg:divide-x lg:divide-app-border">
        <article
          v-for="(item, index) in items"
          :key="item.title"
          class="min-w-0 border-b border-app-border py-6 first:pt-0 last:border-b-0 last:pb-0 lg:border-b-0 lg:px-7 lg:py-0 lg:first:pl-0 lg:last:pr-0"
        >
          <div class="flex items-start gap-4">
            <span
              class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm"
            >
              <component :is="item.icon" class="size-5" />
            </span>

            <div class="min-w-0">
              <p
                class="text-xs font-semibold uppercase tracking-wide text-primary"
              >
                {{ String(index + 1).padStart(2, "0") }}
              </p>

              <h3 class="mt-1 text-base font-bold leading-6 text-app-text">
                {{ $t(item.title) }}
              </h3>

              <p class="mt-2 text-sm leading-6 text-app-muted">
                {{ $t(item.text) }}
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
